import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { SALES_MANAGE_ROLES, SALES_ROLES, hasRole } from '@/auth/roles';
import { deleteLead, getLead } from '@/api/leads.api';
import { getGrades, getSchools } from '@/api/lookups.api';
import { ApiError } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { ConvertLeadDialog } from '@/pages/leads/ConvertLeadDialog';
import { LeadAssignPanel } from '@/pages/leads/LeadAssignPanel';
import { LeadInfoPanel } from '@/pages/leads/LeadInfoPanel';
import { LeadNotesPanel } from '@/pages/leads/LeadNotesPanel';
import { LeadStatusPanel } from '@/pages/leads/LeadStatusPanel';
import { showSuccessToast } from '@/lib/toast';
import { formatDateTime } from '@/lib/format';
import { toneForLeadStatus } from '@/lib/leadStatus';
import type { LeadResponse, LookupItem, StudentResponse } from '@/types/shared';
import './LeadDetailPage.css';

interface LocationState {
  successMessage?: string;
}

export function LeadDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const canEdit = hasRole(user?.role, SALES_ROLES);
  const canManage = hasRole(user?.role, SALES_MANAGE_ROLES);

  const [lead, setLead] = useState<LeadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [grades, setGrades] = useState<LookupItem[]>([]);
  const [schools, setSchools] = useState<LookupItem[]>([]);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [convertOpen, setConvertOpen] = useState(false);

  const locationState = (location.state as LocationState | null) ?? null;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getLead(id)
      .then((data) => {
        if (!cancelled) setLead(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = ApiError.fromUnknown(err);
        setLoadError(
          apiError.code === 'NOT_FOUND' ? t('leads.detail.notFound') : t('leads.detail.loadError'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  useEffect(() => {
    getGrades({ activeOnly: false })
      .then(setGrades)
      .catch(() => setGrades([]));
    getSchools({ activeOnly: false })
      .then(setSchools)
      .catch(() => setSchools([]));
  }, []);

  async function handleConfirmDelete() {
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteLead(id);
      setConfirmDeleteOpen(false);
      const message = t('leads.detail.deleteSuccess');
      showSuccessToast(message);
      navigate('/leads', {
        state: { successMessage: message },
      });
    } catch {
      setDeleteError(t('leads.detail.deleteError'));
    } finally {
      setDeleting(false);
    }
  }

  function handleConverted(student: StudentResponse) {
    const message = t('leads.convert.convertSuccess', { code: student.studentCode });
    showSuccessToast(message);
    navigate(`/students/${student.studentCode}`, {
      state: {
        successMessage: message,
      },
    });
  }

  if (loading) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  if (loadError || !lead) {
    return (
      <div>
        <Banner tone="danger" assertive>
          {loadError ?? t('leads.detail.notFound')}
        </Banner>
        <Link to="/leads" className="fa-back-link">
          <span className="fa-back-link__arrow" aria-hidden="true" />
          {t('leads.detail.backToList')}
        </Link>
      </div>
    );
  }

  const canConvert = lead.status === 'Attended' && !lead.studentCode;

  return (
    <section className="fa-lead-detail">
      <Link to="/leads" className="fa-back-link">
        <span className="fa-back-link__arrow" aria-hidden="true" />
        {t('leads.detail.backToList')}
      </Link>

      {locationState?.successMessage && (
        <Banner tone="success">{locationState.successMessage}</Banner>
      )}
      {deleteError && (
        <Banner tone="danger" assertive>
          {deleteError}
        </Banner>
      )}
      {lead.possibleDuplicate && (
        <Banner tone="warning">{t('leads.detail.possibleDuplicateBanner')}</Banner>
      )}

      <header className="fa-lead-detail__header">
        <div className="fa-lead-detail__heading">
          <div>
            <h1 className="fa-lead-detail__title">{lead.fullName}</h1>
            <p className="fa-lead-detail__subtitle">
              <bdi className="fa-ltr-isolate">{lead.phone}</bdi>
            </p>
            <div className="fa-lead-detail__badges">
              <StatusChip
                label={t(`leads.status.${lead.status}`)}
                tone={toneForLeadStatus(lead.status)}
              />
              <span className="fa-chip fa-chip--neutral">
                {t(`leads.source.${lead.leadSource}`)}
              </span>
              {!canEdit && (
                <span className="fa-chip fa-chip--neutral">{t('leads.detail.readOnlyBadge')}</span>
              )}
            </div>
          </div>
        </div>
        <div className="fa-lead-detail__actions">
          {lead.studentCode && (
            <Link to={`/students/${lead.studentCode}`} className="fa-btn fa-btn--secondary">
              {t('leads.detail.viewStudent')}
            </Link>
          )}
          {canConvert && (
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              onClick={() => setConvertOpen(true)}
            >
              {t('leads.detail.convertButton')}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className="fa-btn fa-btn--destructive"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              {t('leads.detail.deleteButton')}
            </button>
          )}
        </div>
      </header>

      <LeadInfoPanel
        lead={lead}
        canEdit={canEdit}
        grades={grades}
        schools={schools}
        onSaved={setLead}
      />
      <LeadStatusPanel lead={lead} canReopenLost={canManage} onSaved={setLead} />
      <LeadNotesPanel lead={lead} canEdit={canEdit} onSaved={setLead} />
      <LeadAssignPanel lead={lead} canManage={canManage} onSaved={setLead} />

      <section className="fa-form-section fa-lead-detail__audit">
        <h2 className="fa-form-section__title">{t('leads.detail.auditTitle')}</h2>
        <div className="fa-lead-detail__audit-grid">
          <p>
            <span className="fa-lead-detail__audit-label">{t('leads.detail.addedBy')}</span>
            <span>
              {lead.createdBy?.fullName ?? t('leads.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(lead.createdAt, i18n.language)}</bdi>
            </span>
          </p>
          <p>
            <span className="fa-lead-detail__audit-label">{t('leads.detail.updatedBy')}</span>
            <span>
              {lead.updatedBy?.fullName ?? t('leads.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(lead.updatedAt, i18n.language)}</bdi>
            </span>
          </p>
        </div>
      </section>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('leads.detail.deleteConfirmTitle')}
        description={t('leads.detail.deleteConfirmBody', { name: lead.fullName })}
        confirmLabel={t('leads.detail.deleteConfirmAction')}
        cancelLabel={t('leads.form.cancel')}
        destructive
        confirmDisabled={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {convertOpen && (
        <ConvertLeadDialog
          lead={lead}
          onClose={() => setConvertOpen(false)}
          onConverted={handleConverted}
        />
      )}
    </section>
  );
}
